import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Button, Field } from "../components/ui";
import { TermsModal } from "../components/Terms";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { apiGet } from "../api";
import { C, R, S } from "../theme";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPw] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [emailStatus, setEmailStatus] = useState(""); // "", "checking", "taken", "ok"

  useEffect(() => {
    const e = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { setEmailStatus(""); return; }
    setEmailStatus("checking");
    const t = setTimeout(async () => {
      try { const r = await apiGet(`/auth/check-availability?email=${encodeURIComponent(e)}`); setEmailStatus(r.email_taken ? "taken" : "ok"); }
      catch { setEmailStatus(""); }
    }, 450);
    return () => clearTimeout(t);
  }, [email]);

  const submit = async () => {
    setErr("");
    if (!company || !email || password.length < 6) { setErr(t("auth.errFillAll")); return; }
    if (emailStatus === "taken") { setErr(t("auth.errEmailRegistered")); return; }
    if (!agreed) { setErr(t("auth.errAcceptTerms")); return; }
    setBusy(true);
    try { await register(email, password, company); nav("/dashboard"); }
    catch (e) { setErr(e?.message || t("auth.registerFailed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="screen fade-up" style={{ padding: 24 }}>
      <button data-testid="back-login" className="pressable" onClick={() => nav(-1)} style={{ marginBottom: 12 }}>
        <Icon name="chevron-back" size={26} color={C.text} />
      </button>
      <h1 className="display" style={{ color: C.text, fontSize: 30, fontWeight: 900, margin: 0 }}>{t("auth.createOwnerAccount")}</h1>
      <div style={{ color: C.muted, fontSize: 15, marginTop: 4, marginBottom: 24 }}>{t("auth.ownerSubtitle")}</div>
      <Field label={t("auth.companyName")} testID="reg-company" value={company} onChange={(e) => setCompany(e.target.value)} placeholder={t("auth.companyPlaceholder")} />
      <Field label={t("auth.email")} testID="reg-email" autoCapitalize="none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")} />
      {emailStatus === "checking" && <div style={{ color: C.muted, fontSize: 12, marginTop: -6, marginBottom: 10 }}>{t("auth.checkingAvailability")}</div>}
      {emailStatus === "taken" && <div data-testid="reg-email-taken" style={{ color: C.red, fontSize: 12, marginTop: -6, marginBottom: 10, fontWeight: 700 }}>{t("auth.emailTaken")}</div>}
      {emailStatus === "ok" && <div data-testid="reg-email-ok" style={{ color: C.green, fontSize: 12, marginTop: -6, marginBottom: 10, fontWeight: 700 }}>{t("auth.emailAvailable")}</div>}
      <Field label={t("auth.password")} testID="reg-password" type="password" value={password} onChange={(e) => setPw(e.target.value)} placeholder={t("auth.passwordMin6Placeholder")} />

      <button
        type="button"
        data-testid="reg-terms-checkbox"
        onClick={() => setAgreed((v) => !v)}
        style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "transparent", border: "none", padding: 0, textAlign: "left", margin: "4px 0 16px", cursor: "pointer" }}
      >
        <span style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0, border: `2px solid ${agreed ? C.green : C.borderStrong}`, background: agreed ? C.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {agreed && <Icon name="checkmark" size={16} color={C.black} />}
        </span>
        <span style={{ color: C.sub, fontSize: 14, lineHeight: 1.5 }}>
          {t("auth.iAgreeTo")}
          <span data-testid="reg-terms-link" onClick={(e) => { e.stopPropagation(); setTermsOpen(true); }} style={{ color: C.amber, fontWeight: 800, textDecoration: "underline" }}>{t("auth.termsAndLegal")}</span>
        </span>
      </button>

      {!!err && <div style={{ color: C.red, marginBottom: 12, fontWeight: 600 }}>{err}</div>}
      <Button title={t("auth.createAccount")} testID="reg-submit" onPress={submit} loading={busy} />

      <button data-testid="reg-footer-terms" className="pressable" onClick={() => setTermsOpen(true)} style={{ display: "block", margin: "18px auto 0", color: C.muted, fontWeight: 700, fontSize: 13, background: "transparent", border: "none", textDecoration: "underline" }}>
        {t("auth.termsLegalShort")}
      </button>

      <div data-testid="register-language-switcher" style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
        <LanguageSwitcher align="center" />
      </div>

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}
